// SCO-2024: proactive update detection. The build stamps its own git commit
// into package.json's `build` field and ships the same stamp in version.json
// (scripts/set-build.js, SCO-2014) — so comparing the two tells a running
// session whether a newer build has been deployed since it loaded.
//
// Reload policy: silent reload only on return-to-foreground after being hidden
// a while (the user hasn't started doing anything); a forced reload mid-use
// could eat scoring or form state, and the old tap-to-update toast read as a
// native app update (Phil, 2026-09-23) — so while in use we do nothing and let
// the reactive dead-chunk reload in index.jsx (SCO-2020/PR #111) catch a stale
// session when it matters. One reload attempt per target commit
// (sessionStorage), so a stale cached index.html (SCO-2020's hosting gap)
// can't reload-loop.
import packageJson from "../../package.json";

const AUTO_RELOAD_MIN_HIDDEN_MS = 5 * 60 * 1000;
const RELOAD_ATTEMPT_KEY = "updateWatcherReloadedFor";

// "-dirty" only differs by uncommitted local changes at build time — treat
// it as the same code so a dirty hotfix build doesn't prompt against itself.
const normalize = (commit) => String(commit || "").replace(/-dirty$/, "");
const runningCommit = normalize(packageJson.build);

async function fetchDeployedCommit() {
  try {
    const resp = await fetch(`/v2/version.json?_=${Date.now()}`, {
      cache: "no-store",
    });
    if (!resp.ok) return null;
    const data = await resp.json();
    return data && typeof data.commit === "string"
      ? normalize(data.commit)
      : null;
  } catch (error) {
    // Offline / transient — try again on the next foreground
    return null;
  }
}

function reloadForUpdate(deployedCommit) {
  let attempted = null;
  try {
    attempted = sessionStorage.getItem(RELOAD_ATTEMPT_KEY);
  } catch (error) {
    // storage unavailable (private mode) — can't guard against a loop, so
    // don't reload at all
    return;
  }
  if (attempted === deployedCommit) {
    // We already reloaded for this commit and came back still-old: index.html
    // itself is being served stale. Reloading again would loop.
    return;
  }
  sessionStorage.setItem(RELOAD_ATTEMPT_KEY, deployedCommit);
  window.location.reload();
}

let checking = false;
async function check() {
  if (checking || !runningCommit) return;
  checking = true;
  try {
    const deployedCommit = await fetchDeployedCommit();
    if (deployedCommit && deployedCommit !== runningCommit) {
      reloadForUpdate(deployedCommit);
    }
  } finally {
    checking = false;
  }
}

function start() {
  let hiddenAt = null;
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      hiddenAt = Date.now();
    } else if (document.visibilityState === "visible") {
      const hiddenForMs = hiddenAt ? Date.now() - hiddenAt : 0;
      hiddenAt = null;
      if (hiddenForMs >= AUTO_RELOAD_MIN_HIDDEN_MS) check();
    }
  });
}

// Dev never has a real version.json (set-build.js only runs in `build`), and
// hot reload makes the comparison meaningless.
if (process.env.NODE_ENV === "production") {
  start();
}
